/**
 * Плитки событий — «день в один тап».
 *
 * Тап создаёт событие мгновенно, без подтверждений и без формы. Ошибиться
 * не страшно: пять секунд на отмену в тосте. Интервальные типы (прогулка,
 * сон) на ходу превращаются в кнопку «стоп» с живым счётчиком.
 */

import clsx from 'clsx'
import { useState } from 'react'

import type { EventType, TimelineEvent } from '@/shared/api/day'
import { formatClock } from '@/shared/format'
import { haptic } from '@/shared/telegram'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { eventIcon, hasEventIcon } from '@/shared/ui/icon-map'

export function EventTiles({
  types,
  running,
  busyTypeId,
  onCreate,
  onStop,
  extra,
}: {
  types: EventType[]
  running: TimelineEvent[]
  busyTypeId?: number | null
  onCreate: (type: EventType) => void
  onStop: (event: TimelineEvent) => void
  extra?: React.ReactNode
}) {
  const runningByType = new Map(running.map((event) => [event.type_id, event]))
  // «Я на месте» — не плитка, а отдельное действие с обязательной геометкой
  // (кнопка в extra у экрана няни). Общей плиткой оно создавалось бы без точки,
  // а при включённых геометках кнопок «Я на месте» становилось две.
  const tiles = types.filter((type) => type.code !== 'here')

  return (
    <div className="grid grid-cols-4 gap-2">
      {tiles.map((type) => {
        const active = runningByType.get(type.id)
        return active ? (
          <RunningTile key={type.id} type={type} event={active} onStop={onStop} />
        ) : (
          <Tile
            key={type.id}
            type={type}
            busy={busyTypeId === type.id}
            onPress={() => onCreate(type)}
          />
        )
      })}
      {extra}
    </div>
  )
}

function Tile({
  type,
  busy,
  onPress,
}: {
  type: EventType
  busy?: boolean
  onPress: () => void
}) {
  // Смена key перезапускает анимацию: значок «кивает» на каждый тап, а не
  // только на первый. Отметить день должно быть приятно физически.
  const [bump, setBump] = useState(0)

  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => {
        haptic('tap')
        setBump((value) => value + 1)
        onPress()
      }}
      className={clsx(
        'press sticker bg-surface flex aspect-square flex-col items-center justify-center gap-1',
        'rounded-[var(--radius-tile)] active:shadow-[var(--shadow-raised)]',
        'disabled:opacity-60',
      )}
    >
      <span
        key={bump}
        aria-hidden
        className={clsx('flex text-[25px] leading-none', bump && 'wobble')}
      >
        {hasEventIcon(type.code) ? (
          <Icon name={eventIcon(type.code)} size={26} />
        ) : (
          type.emoji
        )}
      </span>
      <span className="text-text-muted px-1 text-center text-[11px] leading-tight">
        {type.title}
      </span>
    </button>
  )
}

function RunningTile({
  type,
  event,
  onStop,
}: {
  type: EventType
  event: TimelineEvent
  onStop: (event: TimelineEvent) => void
}) {
  const minutes = Math.floor((Date.now() - new Date(event.occurred_at).getTime()) / 60_000)

  return (
    <button
      type="button"
      onClick={() => {
        haptic('select')
        onStop(event)
      }}
      className={clsx(
        'press bg-accent relative flex aspect-square flex-col items-center justify-center gap-1',
        'rounded-[var(--radius-tile)] text-on-accent',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_6px_18px_-6px_var(--color-accent)]',
      )}
    >
      {/* тихий пульс: событие идёт прямо сейчас */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-[var(--radius-tile)] ring-2 ring-[color-mix(in_srgb,var(--color-accent)_45%,transparent)]"
        style={{ animation: 'pulse 2.4s ease-in-out infinite' }}
      />
      {/* На плотной заливке чернильный контур звучит резко — приглушаем. */}
      <span aria-hidden className="relative flex text-[21px] leading-none">
        {hasEventIcon(type.code) ? (
          <Icon name={eventIcon(type.code)} size={22} className="opacity-90" />
        ) : (
          type.emoji
        )}
      </span>
      <span className="tabular text-[13px] font-bold">{formatClock(minutes)}</span>
      <span className="text-micro text-on-accent/85">стоп</span>
    </button>
  )
}

/** Плитка, которая открывает шторку, а не создаёт событие сразу. */
export function ActionTile({
  icon,
  title,
  tone = 'default',
  onPress,
}: {
  icon: IconName
  title: string
  tone?: 'default' | 'warm'
  onPress: () => void
}) {
  return (
    <button
      type="button"
      onClick={() => {
        haptic('tap')
        onPress()
      }}
      className={clsx(
        'press sticker flex aspect-square flex-col items-center justify-center gap-1',
        'rounded-[var(--radius-tile)] active:shadow-[var(--shadow-raised)]',
        tone === 'warm' ? 'bg-warm-soft' : 'bg-surface',
      )}
    >
      <Icon name={icon} size={26} />
      <span
        className={clsx(
          'px-1 text-center text-micro leading-tight',
          tone === 'warm' ? 'text-warm-ink' : 'text-text-muted',
        )}
      >
        {title}
      </span>
    </button>
  )
}
